import { format, formatDistanceToNow } from 'date-fns';
import { enUS, zhCN } from 'date-fns/locale';
import { formatMoney as formatMoneyShared } from '@wrapforce/shared';
import { useLocaleStore } from '@/stores/locale';
import type { Locale } from '@/lib/i18n';

const DATE_FNS_LOCALES: Record<Locale, typeof enUS> = { en: enUS, 'zh-CN': zhCN };
const INTL_LOCALES: Record<Locale, string> = { en: 'en-US', 'zh-CN': 'zh-CN' };

export function useLocaleFormatters() {
  const locale = useLocaleStore((s) => s.locale);
  const dateFnsLocale = DATE_FNS_LOCALES[locale];
  const intlLocale = INTL_LOCALES[locale];

  return {
    formatMoney: (cents: number, currency = 'usd') => formatMoneyShared(cents, currency, intlLocale),
    formatDate: (date: Date | number, dateFormat: string) => format(date, dateFormat, { locale: dateFnsLocale }),
    formatDateTime: (date: Date | number) => new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium', timeStyle: 'short' }).format(date),
    formatRelative: (date: Date | number) => formatDistanceToNow(date, { addSuffix: true, locale: dateFnsLocale }),
    formatRelativeShort: (date: Date | number) => {
      const seconds = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 1000));
      if (seconds < 60) return `${seconds}s`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
      if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`;
      if (seconds < 2_592_000) return `${Math.floor(seconds / 86_400)}d`;
      if (seconds < 31_536_000) return `${Math.floor(seconds / 2_592_000)}mo`;
      return `${Math.floor(seconds / 31_536_000)}y`;
    },
    // §34.2 - `minutes` is a plain minutes-from-midnight integer (no timezone
    // attached, it's already the shop's local clock time), so this pins the
    // formatter to UTC via a fixed reference date rather than letting the
    // browser's own timezone shift it.
    formatTimeOfDay: (minutes: number) =>
      new Intl.DateTimeFormat(intlLocale, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).format(new Date(minutes * 60_000)),
  };
}
